/**
 * Estate Executors Hooks
 *
 * React Query hooks for executor designation lifecycle (person and firm).
 *
 * @module hooks/useEstateExecutors
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@xanectech/auth';
import {
  EstateExecutorService,
  type CreateExecutorPayload,
  type UpdateExecutorPayload,
} from '../services/EstateExecutorService';
import {
  EstateContactDocumentWorkflowService,
  type DocWorkflowStatusSnapshot,
  type ContactRole,
} from '../services/EstateContactDocumentWorkflowService';
import { createLogger } from '@xanectech/lib';
import { estateLockerKeys } from './useEstateLocker';

const logger = createLogger('useEstateExecutors');

// ============================================
// Query keys
// ============================================

export const executorKeys = {
  list: (userId: string) => ['estate', 'executors', userId] as const,
  workflow: (userId: string, contactId: string) =>
    ['estate', 'executor', 'workflow', userId, contactId] as const,
};

// ============================================
// Hooks
// ============================================

/**
 * List active executors for the authenticated user.
 */
export function useEstateExecutors() {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  return useQuery({
    queryKey: executorKeys.list(userId),
    queryFn: () => EstateExecutorService.listExecutors(userId),
    enabled: !!userId,
  });
}

/**
 * Create a new executor designation.
 */
export function useCreateExecutor() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: (payload: CreateExecutorPayload) =>
      EstateExecutorService.createExecutor(userId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: executorKeys.list(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.overview(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to create executor', {
        context: 'useCreateExecutor',
        error: String(error),
      });
    },
  });
}

/**
 * Update an executor designation.
 */
export function useUpdateExecutor() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      contactId,
      payload,
    }: {
      contactId: string;
      payload: UpdateExecutorPayload;
    }) => EstateExecutorService.updateExecutor(userId, contactId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: executorKeys.list(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to update executor', {
        context: 'useUpdateExecutor',
        error: String(error),
      });
    },
  });
}

/**
 * Revoke an executor designation.
 */
export function useRevokeExecutor() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      contactId,
      reason,
    }: {
      contactId: string;
      reason: string;
    }) => EstateExecutorService.revokeExecutor(userId, contactId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: executorKeys.list(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: estateLockerKeys.overview(userId),
      });
    },
    onError: (error) => {
      logger.error('Failed to revoke executor', {
        context: 'useRevokeExecutor',
        error: String(error),
      });
    },
  });
}

/**
 * Evaluate (or re-evaluate) the document workflow status for an executor.
 * Persists the result into access_scope.doc_workflow and invalidates the list.
 */
export function useEvaluateExecutorDocWorkflow() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      contactId,
      role,
      countryCode,
    }: {
      contactId: string;
      role: ContactRole;
      countryCode: string | null;
    }): Promise<DocWorkflowStatusSnapshot> =>
      EstateContactDocumentWorkflowService.evaluateAndPersist(
        userId,
        contactId,
        role,
        countryCode,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: executorKeys.list(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: executorKeys.workflow(userId, variables.contactId),
      });
    },
    onError: (error) => {
      logger.error('Failed to evaluate executor doc workflow', {
        context: 'useEvaluateExecutorDocWorkflow',
        error: String(error),
      });
    },
  });
}

/**
 * Acknowledge a soft-block on an executor's document workflow.
 */
export function useAcknowledgeExecutorSoftBlock() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? '';

  return useMutation({
    mutationFn: ({
      contactId,
      reason,
    }: {
      contactId: string;
      reason: string;
    }) =>
      EstateContactDocumentWorkflowService.acknowledgeSoftBlock(
        userId,
        contactId,
        reason,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: executorKeys.list(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: executorKeys.workflow(userId, variables.contactId),
      });
    },
    onError: (error) => {
      logger.error('Failed to acknowledge executor soft block', {
        context: 'useAcknowledgeExecutorSoftBlock',
        error: String(error),
      });
    },
  });
}

export type { DocWorkflowStatusSnapshot, ContactRole };
